import { SITE } from './site-config';

export const servicesContent = {
  'personal-training': {
    eyebrow: 'Private 1-on-1 training',
    h1: `Personal Training in ${SITE.city}, ${SITE.stateAbbr}`,
    intro: `One-on-one coaching in a private studio. Your trainer focuses on you for the full session — form, programming, accountability, all of it. No waiting on equipment, no class to keep up with.`,
    metaDescription: `Private 1-on-1 personal training in ${SITE.city}, FL. Programs built around your goals, schedule, and starting point. Coached by Jarad Wingard.`,
    whoFor: [
      'Adults at any starting point — beginners through experienced lifters',
      'People who want focused, individual coaching, not a class',
      'Clients managing schedules or constraints that group training can’t flex around',
      'Anyone returning after time off, injury rehab, or life changes',
    ],
    workOn: [
      'Strength and muscle (resistance training)',
      'Conditioning and fat-loss support',
      'Mobility, posture, and movement quality',
      'Confidence with form on key lifts',
    ],
    included: [
      '1-on-1 coaching in the private Stuart studio',
      'Programming built specifically for you',
      'Form coaching and adjustments every session',
      'Progress tracking and check-ins',
    ],
    whatToExpect: 'We start with a free consultation to understand your goal and starting point. From there, sessions are scheduled to fit your week. You’ll know what you’re doing each session and why — progress is tracked and the plan evolves with you.',
    whyPersonal: `${SITE.owner} coaches every client personally. The program is built for you specifically — not pulled from a template — and the studio is private so the focus stays on your session.`,
    safety: 'Results vary based on consistency, recovery, nutrition, and individual factors. We don’t make medical claims or promise specific weight-loss outcomes.',
    faqs: [
      { q: 'Do I need experience to start?', a: 'No. We coach beginners regularly. Sessions are paced to your level.' },
      { q: 'How often should I train?', a: 'We’ll talk through it on the consultation. Most clients train 2–3 days per week. Frequency depends on your goal, schedule, and recovery.' },
      { q: 'Is the studio private?', a: 'Yes. It’s a private appointment-only studio in south Stuart. Your session is yours.' },
    ],
  },

  'semi-private-personal-training': {
    eyebrow: 'Train with a partner',
    h1: `Semi-Private Personal Training in ${SITE.city}, ${SITE.stateAbbr}`,
    intro: `Train with a spouse, friend, or training partner in our private studio. You each get individual programming and coaching attention — but you split the energy (and the cost) of the session.`,
    metaDescription: `Semi-private personal training in ${SITE.city}, FL. Train with a partner in a private studio with individual programming and shared coaching attention.`,
    whoFor: [
      'Couples or friends who want to train together',
      'People who like the energy of a partner workout but still want real coaching',
      'Clients looking for a lower per-person cost than 1-on-1',
    ],
    workOn: [
      'Strength training, conditioning, and movement quality',
      'Individual programming inside a shared session',
      'Coaching cues, form, and pacing',
    ],
    included: [
      'A shared private session in the studio',
      'Individualized programming for each partner',
      'Coaching attention from Jarad throughout',
    ],
    whatToExpect: 'Both partners get a starting consultation. Programs are individual — even when you’re in the same session you’re working on what you specifically need. We pace the session so each person gets coached.',
    whyPersonal: 'Semi-private at FT360 is not a class. It’s personal training with a partner in the room. The cap stays small so attention is real.',
    safety: 'Each partner is coached as an individual, not as a group. Both partners complete an intake.',
    faqs: [
      { q: 'Can my partner and I have different goals?', a: 'Yes — each partner has their own program inside the session.' },
      { q: 'What if one of us is more advanced?', a: 'Programming is individual. The more advanced partner does what fits them; the newer partner does what fits them.' },
      { q: 'How many people can train together?', a: 'Semi-private is small by design so coaching stays real. We’ll talk about fit during the consultation.' },
    ],
  },

  'nutrition-coaching': {
    eyebrow: 'Habit-based nutrition coaching',
    h1: `Nutrition Coaching in ${SITE.city}, ${SITE.stateAbbr}`,
    intro: `Nutrition coaching focused on habits that fit your real life — not crash diets or short-term cuts. We help you build patterns you can actually keep, in support of your training.`,
    metaDescription: `Nutrition coaching in ${SITE.city}, FL. Habit-based coaching that supports your training and lifestyle. Not medical nutrition therapy.`,
    whoFor: [
      'Clients who want their nutrition to support their training',
      'People tired of cycles of strict diets that don’t last',
      'Anyone who wants structure without obsession',
    ],
    workOn: [
      'Building consistent meal patterns',
      'Protein, vegetables, and hydration as anchors',
      'Eating around your training and your work week',
      'Realistic strategies for travel, family meals, and busy days',
    ],
    included: [
      'Coaching focused on sustainable habits',
      'Targets and check-ins built around you',
      'Adjustments as life and goals change',
    ],
    whatToExpect: 'We talk through what you eat on a normal day, your schedule, and what’s realistic. From there we agree on small, specific habits to work on — not a strict meal plan you can’t live with.',
    whyPersonal: 'There’s no one-size-fits-all plan. We coach the person in front of us — your schedule, your preferences, your kitchen.',
    safety: 'This is general nutrition coaching, not medical nutrition therapy. We don’t diagnose medical conditions or treat disease. If you have a medical condition, please work with your physician or a registered dietitian. We do not guarantee specific weight outcomes.',
    faqs: [
      { q: 'Will I get a meal plan?', a: 'We focus on habits and structure rather than rigid meal plans. The goal is something you can keep for the long term.' },
      { q: 'Can you treat a medical condition?', a: 'No. This is general fitness-focused nutrition coaching, not medical nutrition therapy. Please see your physician or a registered dietitian for medical conditions.' },
      { q: 'Do I have to count macros?', a: 'Not unless that’s helpful for you. We adapt the approach to the person.' },
    ],
  },

  'golf-fitness-training': {
    eyebrow: 'Train to play better and feel better',
    h1: `Golf Fitness Training in ${SITE.city}, ${SITE.stateAbbr}`,
    intro: `Strength, mobility, and rotation work tailored to golfers in ${SITE.county}. The goal: a more durable body that swings better, plays more comfortably, and recovers faster.`,
    metaDescription: `Golf fitness training in ${SITE.city}, FL. Mobility, rotation, and strength work for golfers in Martin County.`,
    whoFor: [
      'Recreational and competitive golfers in Martin County',
      'Players who feel tightness or lose distance over the round',
      'Anyone who wants a body that can keep playing the game they love',
    ],
    workOn: [
      'Hip and thoracic mobility for rotation',
      'Core control and anti-rotation strength',
      'General strength for power and durability',
      'Conditioning so the back nine feels like the front nine',
    ],
    included: [
      'Individual screening of how you move',
      'Programming built around what your body needs',
      'Progressive coaching session by session',
    ],
    whatToExpect: 'A screen of your mobility, stability, and movement. From there we coach the work that gives the most return for your time — with the goal of feeling better and moving better on the course.',
    whyPersonal: 'Golf-fitness is individual. What helps one golfer would not help another. The work is built around your assessment.',
    safety: 'We do not guarantee distance gains or injury prevention. We are a fitness studio, not a medical clinic — if you have an injury, please clear training with your physician first.',
    faqs: [
      { q: 'Do I need a gym background?', a: 'No. We meet you where you are. Beginners welcome.' },
      { q: 'Will this fix my swing?', a: 'Golf-fitness supports the body that swings the club. For technique, work with a swing instructor; we focus on movement and strength.' },
      { q: 'How often should I train?', a: 'Most golfers do well with 2 sessions per week. We discuss what fits during the consultation.' },
    ],
  },

  'online-fitness-coaching': {
    eyebrow: 'Coaching at a distance',
    h1: 'Online Fitness Coaching',
    intro: `Personal programming and check-ins for clients who can’t train in person at the studio. Built for travel, distance, or schedules that don’t fit appointment-based training.`,
    metaDescription: `Online fitness coaching with individual programming and regular check-ins from Jarad Wingard at Fitness Training 360.`,
    whoFor: [
      'Clients out of the immediate ' + SITE.city + ' area',
      'Travelers and remote workers who need flexible coaching',
      'Past in-person clients who moved and want to keep training with us',
      'People who want individual programming without the in-person session',
    ],
    workOn: [
      'Strength and conditioning programs built for your gym',
      'Form review on submitted video clips',
      'Habit-based nutrition guidance to support training',
      'Check-ins and program updates',
    ],
    included: [
      'Individual programming, not a downloaded template',
      'Regular communication and check-ins',
      'Adjustments as your training and life evolve',
    ],
    whatToExpect: 'We start with a consultation to understand your goal, your schedule, and what gym/equipment you have. From there you receive a program built for you and we stay in touch as it progresses.',
    whyPersonal: 'Online with us is still personal. The plan is built for you specifically, and you have a real human coach you communicate with.',
    safety: 'Availability is limited by Jarad’s schedule. We don’t make medical claims or promise specific outcomes.',
    faqs: [
      { q: 'Do I need a gym membership?', a: 'It helps, but we can also build a home-gym program if that’s your setup. We discuss your equipment in the consultation.' },
      { q: 'How often will we communicate?', a: 'Most online clients have a regular weekly check-in plus on-demand communication for questions.' },
      { q: 'Can I do online and in-person?', a: 'Some local clients combine both. We can talk through what works during the consultation.' },
    ],
  },
};
